import { createRoot, type Root } from "react-dom/client";
import { readConfig } from "./lib/config";
import { buildVarStyle, FONTS_HREF, styleText } from "./style";
import { EventListWidget } from "./widget";

const SELECTOR = '[data-gitg-component="event-list"]';
const MOUNT_ATTR = "data-gitg-mounted";
const FONTS_LINK_ID = "gitg-embed-fonts";

const roots = new WeakMap<HTMLElement, Root>();

function ensureFontsLink() {
  if (document.getElementById(FONTS_LINK_ID)) return;
  const link = document.createElement("link");
  link.id = FONTS_LINK_ID;
  link.rel = "stylesheet";
  link.href = FONTS_HREF;
  document.head.appendChild(link);
}

function mountNode(node: HTMLElement) {
  if (node.getAttribute(MOUNT_ATTR) === "true") {
    return;
  }

  const config = readConfig(node);

  ensureFontsLink();

  const shadowRoot = node.shadowRoot ?? node.attachShadow({ mode: "open" });
  shadowRoot.replaceChildren();

  const varStyle = document.createElement("style");
  varStyle.textContent = buildVarStyle(config);

  const style = document.createElement("style");
  style.textContent = styleText;

  const mountPoint = document.createElement("div");
  shadowRoot.append(varStyle, style, mountPoint);

  const root = createRoot(mountPoint);
  roots.set(node, root);
  node.setAttribute(MOUNT_ATTR, "true");
  root.render(<EventListWidget config={config} />);
}

function mountAll() {
  const nodes = document.querySelectorAll<HTMLElement>(SELECTOR);
  nodes.forEach((node) => mountNode(node));
}

function bootstrap() {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", mountAll, { once: true });
    return;
  }

  mountAll();
}

declare global {
  interface Window {
    GitgEmbed?: {
      mount: (node: HTMLElement) => void;
      mountAll: () => void;
    };
  }
}

window.GitgEmbed = {
  mount: mountNode,
  mountAll,
};

bootstrap();
